import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RELEVANCE_TAGS } from "@/lib/constants";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";

const EMPTY = {
  headline: "", published_date: "", source_org: "", source_url: "", summary: "",
  what_changed: "", why_it_matters: "", who_affected: "", relevance_tags: [], commentary: "", status: "Draft",
};

export default function IntelligenceManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {item or EMPTY}

  const load = async () => {
    try {
      const { data } = await api.get("/admin/intelligence");
      setItems(data.items || []);
    } catch { toast.error("Could not load intelligence items."); }
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed."); }
  };

  const publish = (it) => act(() => api.post(`/admin/intelligence/${it.item_id}/publish`), "Published.");
  const unpublish = (it) => act(() => api.post(`/admin/intelligence/${it.item_id}/unpublish`), "Unpublished (now Draft).");
  const remove = (it) => {
    if (!window.confirm(`Delete "${it.headline}"? This cannot be undone.`)) return;
    act(() => api.delete(`/admin/intelligence/${it.item_id}`), "Deleted.");
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-6 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="kicker">PFAS Risk Watch · Editorial control</div>
          <p className="mt-2 text-sm text-white/55 max-w-2xl">
            Only items with status <span className="text-emerald-300">Published</span> appear on /risk-watch. Research the source, write an original summary, add the source URL, then publish deliberately.
          </p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="intel-add-btn" className="bg-white text-[#0A0B0E] hover:bg-white/90 rounded-none">
          <Plus className="w-3.5 h-3.5 mr-2" /> Add intelligence item
        </Button>
      </div>

      {editing && (
        <IntelForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div className="mt-8 border border-white/10 overflow-auto admin-scroll">
        <table className="min-w-[1000px] w-full text-sm">
          <thead className="bg-[#12141A]">
            <tr className="text-left text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/50">
              <th className="p-3">Date</th><th className="p-3">Status</th><th className="p-3">Headline</th>
              <th className="p-3">Source</th><th className="p-3">Tags</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-white/40" data-testid="intel-empty">No intelligence items yet. Nothing is published on /risk-watch.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.item_id} data-testid={`intel-row-${it.item_id}`} className="border-t border-white/[0.06] hover:bg-white/[0.02]">
                <td className="p-3 whitespace-nowrap">{it.published_date}</td>
                <td className="p-3">
                  <span className={`inline-block border px-2 py-1 text-[10px] font-mono-alt tracking-[0.14em] uppercase ${
                    it.status === "Published" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]" : "border-amber-500/40 text-amber-300 bg-amber-500/[0.08]"}`}
                    data-testid={`intel-status-${it.item_id}`}>{it.status}</span>
                </td>
                <td className="p-3 max-w-[420px]"><div className="truncate text-white/90">{it.headline}</div></td>
                <td className="p-3 whitespace-nowrap">
                  <a href={it.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-white/70 hover:text-white">
                    {it.source_org} <ExternalLink className="w-3 h-3 opacity-40" />
                  </a>
                </td>
                <td className="p-3 text-xs text-white/50">{(it.relevance_tags || []).join(", ") || "—"}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <IconBtn testid={`intel-edit-${it.item_id}`} onClick={() => setEditing(it)} title="Edit"><Pencil className="w-3.5 h-3.5" /></IconBtn>
                    {it.status === "Published"
                      ? <IconBtn testid={`intel-unpublish-${it.item_id}`} onClick={() => unpublish(it)} title="Unpublish"><EyeOff className="w-3.5 h-3.5" /></IconBtn>
                      : <IconBtn testid={`intel-publish-${it.item_id}`} onClick={() => publish(it)} title="Publish" tone="emerald"><Eye className="w-3.5 h-3.5" /></IconBtn>}
                    <IconBtn testid={`intel-delete-${it.item_id}`} onClick={() => remove(it)} title="Delete" tone="red"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, tone }) {
  const cls = { emerald: "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10",
                red: "border-red-500/40 text-red-300 hover:bg-red-500/10" }[tone] || "border-white/20 text-white/70 hover:bg-white/[0.06]";
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid} className={`border ${cls} p-2 transition-colors`}>{children}</button>
  );
}

function IntelForm({ initial, onCancel, onSaved }) {
  const [v, setV] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const toggleTag = (t) => setV((s) => ({
    ...s, relevance_tags: s.relevance_tags.includes(t) ? s.relevance_tags.filter((x) => x !== t) : [...s.relevance_tags, t],
  }));

  const save = async (status) => {
    for (const k of ["headline", "published_date", "source_org", "source_url", "summary"]) {
      if (!v[k]?.trim()) return toast.error(`Please complete: ${k.replace(/_/g, " ")}`);
    }
    setSaving(true);
    try {
      const payload = { ...v, status };
      if (v.item_id) await api.put(`/admin/intelligence/${v.item_id}`, payload);
      else await api.post("/admin/intelligence", payload);
      toast.success(status === "Published" ? "Saved and published." : "Saved as draft.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const inCls = "bg-[#12141A] border-white/10 rounded-none h-10";
  const taCls = "bg-[#12141A] border-white/10 rounded-none";

  return (
    <div className="mt-6 border border-white/10 bg-[#0C0E13] p-6 space-y-5" data-testid="intel-form">
      <div className="kicker">{v.item_id ? "Edit intelligence item" : "New intelligence item"}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <F label="Headline *" className="md:col-span-2"><Input data-testid="intel-headline" value={v.headline} onChange={set("headline")} className={inCls} /></F>
        <F label="Publication date *"><Input type="date" data-testid="intel-date" value={v.published_date} onChange={set("published_date")} className={inCls} /></F>
        <F label="Source organisation *"><Input data-testid="intel-source-org" value={v.source_org} onChange={set("source_org")} placeholder="e.g. Environment Agency" className={inCls} /></F>
        <F label="Original source URL *" className="md:col-span-2"><Input data-testid="intel-source-url" value={v.source_url} onChange={set("source_url")} placeholder="https://" className={inCls} /></F>
        <F label="Short summary *" className="md:col-span-2"><Textarea rows={3} data-testid="intel-summary" value={v.summary} onChange={set("summary")} className={taCls} /></F>
        <F label="What changed?"><Textarea rows={3} data-testid="intel-what-changed" value={v.what_changed} onChange={set("what_changed")} className={taCls} /></F>
        <F label="Why does this matter?"><Textarea rows={3} data-testid="intel-why-matters" value={v.why_it_matters} onChange={set("why_it_matters")} className={taCls} /></F>
        <F label="Who may be affected?" className="md:col-span-2"><Textarea rows={2} data-testid="intel-who-affected" value={v.who_affected} onChange={set("who_affected")} className={taCls} /></F>
        <F label="Relevance tags" className="md:col-span-2">
          <div className="flex flex-wrap gap-2">
            {RELEVANCE_TAGS.map((t) => {
              const on = v.relevance_tags.includes(t);
              return (
                <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`intel-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`border px-3 py-1.5 text-[10px] font-mono-alt tracking-[0.14em] uppercase transition-colors ${
                    on ? "bg-white text-[#0A0B0E] border-white" : "border-white/20 text-white/60 hover:border-white/50"}`}>{t}</button>
              );
            })}
          </div>
        </F>
        <F label="PFAS Risk Intelligence commentary" className="md:col-span-2"><Textarea rows={4} data-testid="intel-commentary" value={v.commentary} onChange={set("commentary")} className={taCls} /></F>
      </div>
      <div className="flex flex-wrap gap-3 pt-2">
        <Button disabled={saving} onClick={() => save("Draft")} data-testid="intel-save-draft-btn" variant="outline" className="border-white/20 text-white hover:bg-white/[0.05] rounded-none">Save as draft</Button>
        <Button disabled={saving} onClick={() => save("Published")} data-testid="intel-save-publish-btn" className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 rounded-none">Save &amp; publish</Button>
        <Button disabled={saving} onClick={onCancel} data-testid="intel-cancel-btn" variant="ghost" className="text-white/60 hover:text-white rounded-none">Cancel</Button>
      </div>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/50">{label}</div>
      {children}
    </div>
  );
}
